/**
 * TagBadge Component
 *
 * A colored badge for displaying a single accounting tag.
 * Supports different sizes, source indicators, and optional remove button.
 *
 * @example
 * // Basic usage
 * <TagBadge tag={vendorTag} />
 *
 * @example
 * // With source indicator and remove button
 * <TagBadge
 *   tag={vendorTag}
 *   source="llm"
 *   confidence={0.95}
 *   removable
 *   onRemove={() => handleRemove(tag)}
 * />
 */

import clsx from 'clsx';
import { X, Bot, Cog, User, FileInput, type LucideIcon } from 'lucide-react';
import type { Tag, TaggingSource } from '@/types/accountingTags';

export interface TagBadgeProps {
  /** The tag to display (can be a partial Tag with required fields) */
  tag: Pick<Tag, 'id' | 'tagType' | 'value' | 'displayName' | 'color'>;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Whether to show a remove button */
  removable?: boolean;
  /** Callback when remove button is clicked */
  onRemove?: () => void;
  /** The source of the tagging (for indicator) */
  source?: TaggingSource;
  /** Confidence score (0-1) */
  confidence?: number | null;
  /** Whether the badge is clickable */
  onClick?: () => void;
}

/**
 * Source indicator icons and labels
 */
const SOURCE_CONFIG: Record<TaggingSource, { Icon: LucideIcon; label: string }> = {
  rule: { Icon: Cog, label: 'Applied by rule' },
  manual: { Icon: User, label: 'Manually tagged' },
  import: { Icon: FileInput, label: 'From import' },
  llm: { Icon: Bot, label: 'AI suggested' },
};

/**
 * Darken a hex color by a percentage (0-100)
 */
function darkenColor(hexColor: string, percent: number): string {
  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  const factor = 1 - percent / 100;
  const newR = Math.round(r * factor);
  const newG = Math.round(g * factor);
  const newB = Math.round(b * factor);

  return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
}

/**
 * TagBadge displays a tag with optional source indicator and remove button.
 */
export function TagBadge({
  tag,
  size = 'md',
  removable = false,
  onRemove,
  source,
  confidence,
  onClick,
}: TagBadgeProps) {
  // Guard against undefined tag
  if (!tag) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-red-100 text-red-700 border border-red-200">
        Missing tag
      </span>
    );
  }

  const displayName = tag.displayName || tag.value;
  const sourceConfig = source ? SOURCE_CONFIG[source] : null;

  // Size classes
  const sizeClasses = {
    sm: 'px-1.5 py-0.5 text-xs gap-1',
    md: 'px-2 py-0.5 text-sm gap-1.5',
    lg: 'px-2.5 py-1 text-sm gap-2',
  };

  const iconSizes = {
    sm: 10,
    md: 12,
    lg: 14,
  };

  // Style based on tag color - use light background with darkened text for good contrast
  const textColor = tag.color ? darkenColor(tag.color, 30) : undefined;
  const bgStyle = tag.color
    ? {
        backgroundColor: `${tag.color}18`, // ~9% opacity for light background
        borderColor: `${tag.color}40`, // 25% opacity for border
        color: textColor,
      }
    : {};

  return (
    <span
      className={clsx(
        'inline-flex items-center rounded-full font-medium border',
        sizeClasses[size],
        !tag.color && 'bg-gray-100 border-gray-200 text-gray-700',
        onClick && 'cursor-pointer hover:opacity-80',
        removable && 'pr-1'
      )}
      style={bgStyle}
      data-testid="tag-badge"
      data-tag-type={tag.tagType}
      data-tag-value={tag.value}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      {/* Tag type prefix */}
      <span className={clsx('font-normal opacity-70', !tag.color && 'text-gray-500')}>
        {tag.tagType}:
      </span>

      {/* Tag value */}
      <span className={clsx('font-medium', !tag.color && 'text-gray-800')}>
        {displayName}
      </span>

      {/* Source indicator */}
      {sourceConfig && (
        <span
          className={clsx('inline-flex items-center opacity-70', !tag.color && 'text-gray-500')}
          title={
            confidence
              ? `${sourceConfig.label} (${Math.round(confidence * 100)}% confidence)`
              : sourceConfig.label
          }
        >
          <sourceConfig.Icon size={iconSizes[size]} aria-hidden="true" />
        </span>
      )}

      {/* Remove button */}
      {removable && onRemove && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className={clsx(
            'inline-flex items-center justify-center rounded-full',
            'hover:bg-black/10 transition-colors',
            size === 'sm' && 'w-3.5 h-3.5',
            size === 'md' && 'w-4 h-4',
            size === 'lg' && 'w-5 h-5',
            !tag.color && 'text-gray-500 hover:text-gray-700'
          )}
          aria-label={`Remove ${displayName} tag`}
          data-testid="tag-remove-button"
        >
          <X size={iconSizes[size]} aria-hidden="true" />
        </button>
      )}
    </span>
  );
}

export default TagBadge;
